import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getAllVerifiedLocations, queryKeys } from '@vgrubs/components';
import { SimpleLocationEntity } from 'components/src/types/location.types';

const PRELOAD_ROOT_MARGIN = '1000px';
const PAGE_SIZE = 20;

const fetchVerifiedLocations = async ({
  pageParam = 1,
  query = '',
}: {
  pageParam?: number;
  query?: string;
}) => {
  const { data } = await getAllVerifiedLocations({
    limit: PAGE_SIZE,
    page: pageParam,
    query: query || null,
    deleted: false,
  });

  const locations = data?.data ?? [];
  const nextPage = locations.length === PAGE_SIZE ? pageParam + 1 : undefined;

  return { locations, nextPage };
};

export const useImportPickerLocations = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const observerRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useMemo(() => debounce(setQuery, 500), []);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isLoading,
  } = useInfiniteQuery({
    queryKey: queryKeys.admin.verifiedLocations.list(query, false),
    queryFn: ({ pageParam = 1 }) =>
      fetchVerifiedLocations({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!enabled) return;
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => observer.disconnect();
  }, [enabled, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    debouncedSearch(e.target.value);
  };

  const resetSearch = () => {
    setInputValue('');
    setQuery('');
    debouncedSearch.cancel();
  };

  const locations: SimpleLocationEntity[] =
    data?.pages.flatMap((page) => page.locations) ?? [];

  return {
    locations,
    inputValue,
    handleInputChange,
    observerRef,
    isFetchingNextPage,
    hasNextPage,
    status,
    resetSearch,
    query,
    isLoading,
  };
};
